import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAtomValue } from 'jotai/react';

import useRemainingTimer from 'src/hooks/useRemainingTimer';
import { useSynchronizedInterval } from 'src/hooks/useSynchronizedInterval';
import { userInventoryAtom } from 'src/stores/auth/atoms';
import type { InventoryItem, InventoryItemCategoryId } from 'src/types/itemInventory';
import { hasItemInInventory } from 'src/utils/itemInventory';

export const useInventoryItem = (predicate: (item: InventoryItem) => boolean) => {
  const userInventory = useAtomValue(userInventoryAtom);

  return userInventory.find(predicate);
};

export function useHasInventoryItem(categoryId: InventoryItemCategoryId) {
  const userInventory = useAtomValue(userInventoryAtom);

  const [hasItem, setHasItem] = useState(hasItemInInventory(userInventory, categoryId));

  const { start, stop } = useSynchronizedInterval(() => {
    setHasItem(hasItemInInventory(userInventory, categoryId));
  });

  useEffect(() => {
    start();

    return () => {
      stop();
    };
  }, [start, stop]);

  return hasItem;
}

interface UseCallbackIfHasItemProps {
  onAbsent?: () => void;
}

export function useCallbackIfHasItem<T extends (...args: unknown[]) => unknown>(
  callback: T,
  categoryId: InventoryItemCategoryId,
  { onAbsent }: UseCallbackIfHasItemProps = {}
) {
  const hasItem = useHasInventoryItem(categoryId);

  return useCallback(
    (...props: Parameters<T>) => {
      if (!hasItem) {
        onAbsent?.();
        return;
      }

      callback(...props);
    },
    [hasItem, callback, onAbsent]
  );
}

export const useItemRemainingSeconds = (categoryId: string): number | undefined => {
  const userInventory = useAtomValue(userInventoryAtom);

  const dateValidUntil = useMemo(() => {
    return userInventory.find((item) => item.categoryId === categoryId)?.dateValidUntil;
  }, [userInventory, categoryId]);

  const { remainingTime, start, stop } = useRemainingTimer(
    dateValidUntil ? new Date(dateValidUntil) : new Date()
  );

  useEffect(() => {
    if (dateValidUntil == null) {
      return;
    }

    start();

    return () => {
      stop();
    };
  }, [dateValidUntil, start, stop]);

  return dateValidUntil ? remainingTime.seconds : undefined;
};
